// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes, invariant } from '../../helpers'

let autoIncrementingId = 0

/**
 * The `<qds-dialog>` element represent a dialog box or other interactive
 * component, such as a dismissible alert, inspector, or subwindow.
 *
 * @see https://quartz.se.com/build/components/dialog
 */
@Component({
  tag: 'qds-dialog',
  shadow: true,
  styleUrl: 'dialog.css',
})
export class Dialog implements ComponentInterface {
  /**
   * Indicates whether or not the dialog is open. You can toggle this attribute
   * to show and hide the dialog, or you can use the `show()` and `close()`
   * methods and this attribute will reflect the dialog’s open state.
   */
  @Prop({ mutable: true, reflect: true }) public open = false

  /**
   * Adds a close button to the dialog.
   */
  @Prop() public closable?: boolean

  @Element() private readonly host!: HTMLElement

  /**
   * Emitted when the user instructs the browser that they wish to dismiss the
   * currently open dialog. The browser fires this event when the user presses
   * the `Esc` key.
   *
   * When a dialog is dismissed with the `Esc` key, both the `qdsCancel` and
   * `qdsClose` events are fired.
   */
  @Event({ eventName: 'qdsCancel', bubbles: false, composed: false })
  private readonly cancelEmitter!: EventEmitter<void>

  /** Emitted when the dialog has been closed. */
  @Event({ eventName: 'qdsClose', bubbles: false, cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  @State() private hasTitleSlot = false

  @State() private hasFooterSlot = false

  #inheritedAttributes: Attributes = {}

  #dialog?: HTMLDialogElement

  #titleId?: string

  @Watch('open')
  protected openChanged(): void {
    if (this.open) this.show()
    else this.close()
  }

  public componentWillRender(): void {
    this.hasTitleSlot = !!this.host.querySelector('[slot="title"]')
    this.hasFooterSlot = !!this.host.querySelector('[slot="footer"]')
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)

    autoIncrementingId += 1
    this.#titleId = `qds-dialog-title-${autoIncrementingId}`
  }

  public componentDidLoad(): void {
    if (this.open) this.show()
  }

  public render() {
    const hasHeading = this.hasTitleSlot || this.closable === true
    return (
      <Host>
        <dialog
          class="qds-dialog"
          ref={this.#ref}
          onCancel={this.#onCancel}
          onClose={this.#onClose}
          onSubmit={this.#onSubmit}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
          aria-labelledby={this.hasTitleSlot ? this.#titleId : undefined}
        >
          {hasHeading && (
            <div class="qds-heading">
              {this.hasTitleSlot && (
                <div id={this.#titleId}>
                  <slot name="title" />
                </div>
              )}
              {this.closable === true && (
                <qds-button
                  class="qds-close-button"
                  text="close dialog"
                  importance="subdued"
                  icon-name="close"
                  icon-library="core"
                  onClick={this.#handleCloseClick}
                  iconOnly
                />
              )}
            </div>
          )}

          <div class="qds-content">
            <slot />
          </div>

          {this.hasFooterSlot && (
            <div class="qds-footer">
              <slot name="footer" />
            </div>
          )}
        </dialog>
        <div class="qds-backdrop" />
      </Host>
    )
  }

  public show(): void {
    invariant(this.#dialog)

    this.#dialog.showModal()
    this.open = this.#dialog.open
  }

  public close(): void {
    invariant(this.#dialog)

    this.#dialog.close()
  }

  readonly #ref = (dialog?: HTMLDialogElement): void => {
    this.#dialog = dialog
  }

  readonly #onCancel = (event: Event): void => {
    if (this.cancelEmitter.emit().defaultPrevented) event.preventDefault()
  }

  readonly #onClose = (): void => {
    invariant(this.#dialog)

    this.closeEmitter.emit()
    this.open = this.#dialog.open
  }

  readonly #handleCloseClick = (): void => {
    this.close()
  }

  readonly #onSubmit = (event: Event): void => {
    if (
      (event instanceof SubmitEvent &&
        (event.submitter instanceof HTMLButtonElement ||
          (event.submitter instanceof HTMLInputElement &&
            event.submitter.formMethod === 'dialog'))) ||
      (event.target instanceof HTMLFormElement &&
        event.target.method === 'dialog')
    ) {
      event.preventDefault()
      this.close()
    }
  }
}
